import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import useLenis from "@/hooks/useLenis";
import Landing from "@/pages/Landing";
import { SupportChat } from "@/components/SupportChat";
import { CookieBanner } from "@/components/CookieBanner";

// Découpage par page : chaque route ne télécharge que son propre code (vitesse de chargement)
const Login = lazy(() => import("@/pages/Login"));
const MediumLogin = lazy(() => import("@/pages/MediumLogin"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Consultation = lazy(() => import("@/pages/Consultation"));
const MediumSpace = lazy(() => import("@/pages/MediumSpace"));
const MediumProfile = lazy(() => import("@/pages/MediumProfile"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const OracleTirage = lazy(() => import("@/pages/OracleTirage"));
const Astrologie = lazy(() => import("@/pages/Astrologie"));
const PremiumSuccess = lazy(() => import("@/pages/PremiumSuccess"));
const Horoscope = lazy(() => import("@/pages/Horoscope"));
const HoroscopeSign = lazy(() => import("@/pages/HoroscopeSign"));
const Compatibilite = lazy(() => import("@/pages/Compatibilite"));
const Numerologie = lazy(() => import("@/pages/Numerologie"));
const ThemeAstral = lazy(() => import("@/pages/ThemeAstral"));
const Faq = lazy(() => import("@/pages/Faq"));
const SeoPage = lazy(() => import("@/pages/SeoPages"));
const GuideBelline = lazy(() => import("@/pages/GuideBelline"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const EmailSuccess = lazy(() => import("@/pages/EmailSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const LeaveReview = lazy(() => import("@/pages/LeaveReview"));
const ConditionsGenerales = lazy(() => import("@/pages/ConditionsGenerales"));
const AvisVerification = lazy(() => import("@/pages/AvisVerification"));
const MentionsLegales = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.MentionsLegales })));
const Confidentialite = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Confidentialite })));
const Cookies = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Cookies })));
const QuiSommesNous = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.QuiSommesNous })));
const Postuler = lazy(() => import("@/pages/Postuler"));
const Rejoindre = lazy(() => import("@/pages/Rejoindre"));
const Activation = lazy(() => import("@/pages/Activation"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
    <span className="font-serif text-2xl gold-shimmer gold-shimmer-anim">Voyance Étoilée</span>
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-gold font-mono text-xs tracking-widest">CHARGEMENT...</div>;
  if (!user) return <Navigate to={`/connexion?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function ProtectedMedium({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-gold font-mono text-xs tracking-widest">CHARGEMENT...</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role !== "medium" && user.role !== "admin") return <Navigate to="/espace" replace />;
  return children;
}

/** Remonte en haut de page à chaque navigation (sinon on reste scotché en bas après un clic footer).
 *  Les liens avec ancre (/#avis, /#mediums…) visent toujours la bonne section. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const go = () => {
      if (hash) {
        const el = document.querySelector(hash);
        if (el) { el.scrollIntoView({ block: "start" }); return; }
      }
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    };
    go();
    const t1 = setTimeout(go, 400);   // contenu chargé en async (médiums, avis)
    const t2 = setTimeout(go, 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pathname, hash]);
  return null;
}

function AppInner() {
  useLenis();
  return (
    <>
    <ScrollToTop />
    <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[999] focus:bg-[#171334] focus:text-goldsoft focus:px-4 focus:py-2 focus:rounded-full focus:text-sm">Aller au contenu principal</a>
    <main id="main-content">
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/voyance/:slug" element={<MediumProfile />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/tirage-oracle" element={<OracleTirage />} />
      <Route path="/astrologie" element={<Astrologie />} />
      <Route path="/livret-premium/succes" element={<PremiumSuccess />} />
      <Route path="/horoscope" element={<Horoscope />} />
      <Route path="/horoscope/:sign" element={<HoroscopeSign />} />
      <Route path="/compatibilite" element={<Compatibilite />} />
      <Route path="/compatibilite/:sign1/:sign2" element={<Compatibilite />} />
      <Route path="/numerologie" element={<Numerologie />} />
      <Route path="/theme-astral" element={<ThemeAstral />} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/acces-medium" element={<MediumLogin />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/avis-verification" element={<AvisVerification />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />
      <Route path="/voyance-en-ligne" element={<SeoPage pageKey="voyance-en-ligne" />} />
      <Route path="/voyance-par-telephone" element={<SeoPage pageKey="voyance-par-telephone" />} />
      <Route path="/voyance-sans-cb" element={<SeoPage pageKey="voyance-sans-cb" />} />
      <Route path="/voyance-pas-chere" element={<SeoPage pageKey="voyance-pas-chere" />} />
      <Route path="/voyance-audiotel" element={<SeoPage pageKey="voyance-audiotel" />} />
      <Route path="/tarot-oui-non" element={<SeoPage pageKey="tarot-oui-non" />} />
      <Route path="/voyance-par-tchat" element={<SeoPage pageKey="voyance-par-tchat" />} />
      <Route path="/voyance-par-email" element={<SeoPage pageKey="voyance-par-email" />} />
      <Route path="/voyance-gratuite" element={<SeoPage pageKey="voyance-gratuite" />} />
      <Route path="/tirage-amoureux-gratuit" element={<SeoPage pageKey="tirage-amoureux-gratuit" />} />
      <Route path="/tirage-tarot-de-marseille" element={<SeoPage pageKey="tirage-tarot-de-marseille" />} />
      <Route path="/theme-astral-gratuit" element={<SeoPage pageKey="theme-astral-gratuit" />} />
      <Route path="/conditions-generales" element={<ConditionsGenerales />} />
      <Route path="/postuler" element={<Postuler />} />
      <Route path="/rejoindre/:token" element={<Rejoindre />} />
      <Route path="/activation/:token" element={<Activation />} />
      <Route path="/guide-oracle-de-belline" element={<GuideBelline />} />
      <Route path="/espace" element={<Protected><Dashboard /></Protected>} />
      <Route path="/consultation/:id" element={<Protected><Consultation /></Protected>} />
      <Route path="/avis/:cid" element={<Protected><LeaveReview /></Protected>} />
      <Route path="/medium" element={<ProtectedMedium><MediumSpace /></ProtectedMedium>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/email/success" element={<Protected><EmailSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </main>
      <SupportChat />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppInner />
          <Toaster theme="dark" position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
